import DocumentViewModal from "@/components/DocumentViewModal";
import DownloadFile from "@/components/DownloadFile";
import ShowLoader from "@/components/Loader";
import ScrollToTop from "@/components/ScrollToTop";
import { ArrowLeftIcon, EyeIcon } from "@heroicons/react/24/solid";
import { Typography, Button } from "@material-tailwind/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { PerformanceDashboard } from ".";

const InfoField = ({ label, value, children }) => (
    <div className="py-3">
        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">{label}</p>
        <p className="text-sm font-medium text-gray-900">{value || "—"}</p>
        {children}
    </div>
);

const DocField = ({ label, value, docUrl, onView, t }) => (
    <div className="py-3">
        <div className="flex items-center gap-2 mb-1">
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{label}</p>
            {docUrl && (
                <span className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={() => onView(docUrl)}
                        className="p-1.5 rounded-md hover:bg-gray-200 text-gray-700 hover:text-blue-600 transition"
                        title={t("view")}
                    >
                        <EyeIcon className="h-4 w-4" />
                    </button>
                    <DownloadFile fileUrl={docUrl} className="text-gray-700 hover:text-blue-600" />
                </span>
            )}
        </div>
        <p className="text-sm font-medium text-gray-900">{value || "—"}</p>
    </div>
);

const Section = ({ title, children }) => (
    <div className="border-b border-gray-200 last:border-b-0 pb-6 last:pb-0">
        <h3 className="text-base font-semibold text-gray-800 mb-4">{title}</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1">
            {children}
        </div>
    </div>
);

function formatDate(dateStr) {
    if (!dateStr) return "—";
    try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return "—";
        return d.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
    } catch {
        return "—";
    }
}

export function ViewProfile() {
    const navigate = useNavigate();
    const [isShowLoader, setIsShowLoader] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [documentUrl, setDocumentUrl] = useState("");
    const { t } = useTranslation("internal-user");
    const { t: ct } = useTranslation("common");

    const { user, loading } = useSelector((state) => state.user);

    useEffect(() => {
        setIsShowLoader(false);
    }, []);

    const openDocument = (url) => {
        setDocumentUrl(url);
        setIsModalOpen(true);
    };

    if (!user && !loading && !isShowLoader) {
        return (
            <div className="mb-8 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <ScrollToTop />
                <div className="flex flex-col items-center justify-center py-16 text-center">
                    <Typography variant="h6" color="gray" className="mb-4">
                        {t("profile.no_profile_data_found")}
                    </Typography>
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
                    >
                        <ArrowLeftIcon className="h-4 w-4" />
                        {t("back")}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="mb-8 flex flex-col gap-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {(loading || isShowLoader) && <ShowLoader />}
            <ScrollToTop />

            {/* Header */}
            <div className="flex flex-col gap-4">
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800 transition w-fit"
                >
                    <ArrowLeftIcon className="h-4 w-4" />
                    {t("back")}
                </button>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <Typography variant="h5" color="blue-gray" className="font-bold text-gray-900">
                        {t("profile_details_title")}
                    </Typography>
                    <Button
                        type="button"
                        variant="outlined"
                        size="sm"
                        onClick={() => navigate("/dashboard/profile/edit")}
                        className="rounded-lg w-fit"
                    >
                        {t("edit_profile_button")}
                    </Button>
                </div>
            </div>

            {/* Profile Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                {/* Profile Header */}
                <div className="p-6 md:p-8 bg-gray-50/50 border-b border-gray-100">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                        <button
                            type="button"
                            onClick={() => user?.profile_image && openDocument(user.profile_image)}
                            className={`flex-shrink-0 rounded-full overflow-hidden shadow-md ${user?.profile_image ? "cursor-pointer ring-2 ring-offset-2 ring-transparent hover:ring-blue-200 transition" : ""}`}
                        >
                            <img
                                src={user?.profile_image || "/img/default-avatar-profile-icon.avif"}
                                alt={user?.first_name || t("profile_alt")}
                                className="w-24 h-24 object-cover"
                            />
                        </button>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">
                                {user?.first_name} {user?.last_name || ""}
                            </h2>
                            <p className="text-sm font-medium text-gray-600 mt-0.5">{user?.roles?.[0] || "—"}  {user?.parent_id == null ? t("manager_badge") : t("member_badge")}</p>
                            <p className="text-xs font-medium text-gray-500 mt-1">
                                {t("profile.id_with_padding", { id: String(user?.id ?? "").padStart(4, "0") })}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Info Sections */}
                <div className="p-6 md:p-8 flex flex-col gap-6">
                    <Section title={t("contact_info_section")}>
                        <InfoField
                            label={t("phone_number_label")}
                            value={[user?.country_code, user?.mobile_number].filter(Boolean).join(" ") || null}
                        />
                        <InfoField
                            label={t("alternate_phone_label")}
                            value={[user?.alternate_country_code, user?.alternate_number].filter(Boolean).join(" ") || null}
                        />
                        <InfoField label={t("email_address_label")} value={user?.email} />
                        <InfoField label={t("member_since_label")} value={formatDate(user?.created_at)} />
                    </Section>

                    {user?.roles?.[0] !== "Vendor" && (
                        <Section title={t("office_location_section")}>
                            <InfoField label={t("profile.office_name")} value={user?.office_name} />
                            <InfoField label={t("profile.city_name")} value={user?.office?.city_name} />
                            <InfoField label={t("profile.country_name")} value={user?.office?.country_name} />
                            <InfoField
                                label={t("profile.office_incharge")}
                                value={user?.sales_work_type === "office" ? t("yes") : t("profile.no")}
                            />
                        </Section>
                    )}

                    <Section title={t("documents_section")}>
                        <DocField
                            label={t("id_proof_label")}
                            value={user?.residence_id}
                            docUrl={user?.residence_image}
                            onView={openDocument}
                            t={t}
                        />
                    </Section>

                    {user?.roles?.[0] === "Vendor" && (
                        <>
                            <Section title={t("profile.company_address")}>
                                <InfoField label={t("profile.registered_company_name")} value={user?.company_name} />
                                <InfoField
                                    label={t("profile.vendor_business_type")}
                                    value={user?.business_type?.name}
                                />
                                <DocField
                                    label={t("profile.national_address")}
                                    value={user?.kyc_details?.permanent_address}
                                    docUrl={user?.kyc_details?.address_proof_image}
                                    onView={openDocument}
                                    t={t}
                                />
                                <DocField
                                    label={t("profile.commercial_reg_number")}
                                    value={user?.kyc_details?.commercial_reg_number}
                                    docUrl={user?.kyc_details?.commercial_reg_image}
                                    onView={openDocument}
                                    t={t}
                                />
                                <DocField
                                    label={t("profile.vat_certificate_number")}
                                    value={user?.kyc_details?.vat_certificate_number}
                                    docUrl={user?.kyc_details?.vat_certificate_image}
                                    onView={openDocument}
                                    t={t}
                                />
                            </Section>

                            <Section title={t("profile.banking_details")}>
                                <DocField
                                    label={t("profile.account_number")}
                                    value={user?.bank_account?.account_number}
                                    docUrl={user?.bank_account?.passbook_image}
                                    onView={openDocument}
                                    t={t}
                                />
                                <InfoField label={t("profile.bank_iban")} value={user?.bank_account?.bank_swift_code} />
                                <InfoField label={t("profile.beneficiary_account_holder_full_name")} value={user?.bank_account?.first_name} />
                            </Section>

                            <Section title={t("other_documents")}>
                                {user?.other_documents_detail && (
                                    <div className="py-3 sm:col-span-2 lg:col-span-3">
                                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                                            {t("profile.document_details")}
                                        </p>
                                        <p className="text-sm font-medium text-gray-900">{user.other_documents_detail}</p>
                                    </div>
                                )}
                                {user?.other_docs && user.other_docs.length > 0 ? (
                                    user.other_docs.map((doc, index) => (
                                        <div key={index} className="py-3 flex items-start gap-2">
                                            <div>
                                                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                                                    {doc?.document_name || t("profile.document_with_index", { index: index + 1 })}
                                                </p>
                                                <span className="flex items-center gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => openDocument(doc?.file)}
                                                        className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800"
                                                    >
                                                        <EyeIcon className="h-4 w-4" /> {t("view")}
                                                    </button>
                                                    <DownloadFile fileUrl={doc?.file} className="text-gray-700 hover:text-blue-600" />
                                                </span>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="py-3 sm:col-span-2 lg:col-span-3">
                                        <p className="text-sm text-gray-500 italic">{t("profile.no_documents_available")}</p>
                                    </div>
                                )}
                            </Section>
                        </>
                    )}
                </div>
            </div>

            {user.roles[0] == 'Sales Representative' && 
                <PerformanceDashboard user_id={user?.id} />
            }

            {isModalOpen && (
                <DocumentViewModal documentUrl={documentUrl} setIsDocumentViewModalOpen={setIsModalOpen} />
            )}
        </div>
    );
}

export default ViewProfile;
